import { Newsletter } from "./newsletter";
import { ContactForm } from "./contact-form";
import { TipBox } from "./tip-box";
import { Supporters } from "./supporters";
import { SocialLinks } from "./social-links";
import { CodeLink, PageLinkContent, pageLinkAttributes } from "./page-link";
import { sampleSocials } from "../../src/lib/social-icons";
import { formattedTextHtml } from "../../../shared/src/page-formatted-text-runtime";
import { PodcastFollow } from "./podcast/podcast-follow";
import { Fragment, Suspense, createContext, lazy, useContext, type ReactNode } from "react";
import { useHydrated } from "../lib/hydrated";
import type { ExperienceConfig } from "../../../shared/src/experiences";
import { experienceCatalog } from "../../../shared/src/experience-catalog";
import { planAccessLabel } from "../../../shared/src/plan-access";
import { canonicalFeaturePath } from "../../../shared/src/feature-paths";
import { shipsToPhrase } from "../../../shared/src/ships-to";
import { isEntrance } from "../../../shared/src/page-motion-runtime";
import {
  pageListItems,
  pageAccentParts,
  pageTextLinkParts,
  pageColumnShare,
  pageFluidTextFit,
  pageNodeStyle,
  pageNodeTreatments,
  planBenefits,
  planPrice,
} from "../../../shared/src/page-presentation";
import { twitchChannelLogin, twitchChannelUrl } from "../../../shared/src/page-stream";
import {
  pageItemField,
  resolvePageText,
  resolvePageImage,
  resolvePageTooltipEntries,
  type PageTextValues,
} from "../../../shared/src/page-variables";
import { pageNodeVisible, walkPageNodes } from "../../../shared/src/page-tree";
import type { PageNode } from "../../../shared/src/pages";
import { canvasPlaceholderSetting } from "../../../shared/src/canvas-placeholders";
import { CanvasImage } from "./canvas-image";
import { Camera, ImagePlus, Plus } from "lucide-react";
import {
  eagerImageSizes,
  lazyImageSizes,
  repeaterImageSizes,
  routeSrcset,
  type PublicImage,
} from "../../../shared/src/resized-images";
import { textStyleOf, withTextStyle } from "../../../shared/src/text-styles-runtime";
import type { TextStyles } from "../../../shared/src/text-styles";
import type { DocumentPageData } from "../server/document.server";
import { MediaPlayer, PageVideo } from "./media";
import { NativePending } from "./native-pending";
import { emptyLeague } from "./bible/league";
import { LeagueBlockView, LeagueHandbook, LeagueSection, RichText } from "./league/lazy-league";
import { UpdatesFeed } from "./updates-feed";
import { componentInstanceNodes } from "../../../shared/src/page-components-runtime";
import { PageInteraction } from "./page-interaction";
import { SearchableCards } from "./searchable-cards";
import {
  planIntroLabel,
  planMembersLabel,
  planOwnPerks,
  planSavingsLabel,
} from "../../../shared/src/plan-card";
import { PlanMembers, PlanPerks, PlanPrice, PlanSavings } from "./membership/plan-card";
import { PictureFullView } from "./picture-full-view";
import { elementStyled, type ElementStyles } from "../../../shared/src/element-styles-runtime";

type Bindings = NonNullable<Extract<DocumentPageData, { kind: "page" }>["bindings"]>;

/** Where the owner fills each empty part of their site, in their dashboard. */
export type OwnerLinks = {
  creatorId: string;
  brand: string;
  bio: string;
  post: string;
  socials: string;
};

/** One of the owner's own picture slots, their cover or profile picture: its names and its look. */
export type OwnerSlot = {
  kind: "cover" | "logo";
  /** The empty slot's action, then the chosen picture's. */
  add: string;
  change: string;
  /** A small picture (a profile picture, a link page's portrait): a round camera at its foot. */
  compact: boolean;
  /** A picture under words (an overlay's): its control takes the top corner, clear of them. */
  top: boolean;
};

/** The owner's slot for a picture of theirs; the cover is the opening's own picture. */
function ownerSlot(node: Extract<PageNode, { kind: "image" }>, parent?: PageNode): OwnerSlot {
  const logo = node.variable === "creator.logo";
  const cover = node.id === "hero-image";
  const compact = logo || (node.style?.width ?? Infinity) <= 200;
  return {
    kind: logo ? "logo" : "cover",
    add: logo ? "Add a profile picture" : cover ? "Add a cover image" : "Add a picture",
    change: logo ? "Change profile picture" : cover ? "Change cover" : "Change picture",
    compact,
    top: !compact && parent?.kind === "frame" && parent.layout === "overlay",
  };
}

// The owner's private view edits in place (owner-editing.tsx), a chunk visitors never load. Until
// the page is interactive its prompts link to the editor, as the server drew them.
const owned = <Name extends keyof typeof import("./owner-editing")>(name: Name) =>
  lazy(() => import("./owner-editing").then((module) => ({ default: module[name] })));
const OwnerIntro = owned("OwnerIntro");
const OwnerPicture = owned("OwnerPicture");
const OwnerPictureChange = owned("OwnerPictureChange");
const OwnerSocials = owned("OwnerSocials");

/**
 * Drawn in the browser rather than hydrated from the server's markup (the dashboard's Website
 * view): the owner's controls are live from the first render there.
 */
export const DrawnInBrowser = createContext(false);

function WhenEditable({ fallback, children }: { fallback: ReactNode; children: ReactNode }) {
  const hydrated = useHydrated();
  const drawn = useContext(DrawnInBrowser);
  return hydrated || drawn ? <Suspense fallback={fallback}>{children}</Suspense> : <>{fallback}</>;
}
type Item = Bindings["items"][string][number];

export type PageNodesProps = {
  nodes: PageNode[];
  bindings: Bindings;
  experiences: ExperienceConfig[];
  preview: boolean;
  item?: Item;
  creator?: PageTextValues["creator"];
  /** The signed-in visitor, for {{member.name}}; the page is already private to them. */
  member?: PageTextValues["member"];
  textStyles?: TextStyles;
  /** The creator's shared element styles, drawn under each linked element's own settings. */
  elementStyles?: ElementStyles;
  /** The top-level section these nodes sit in; its entrances share one timeline. */
  section?: string;
  /** A repeated item's place in its collection, so its entrances follow one another. */
  index?: number;
  priorityImageId?: string;
  /** The page's opening sections; a feed outside them loads its pictures as they scroll near. */
  openingSectionIds?: string[];
  /** The published pictures this page links, with their resized copies. */
  images: Readonly<Record<string, PublicImage>>;
  /**
   * The feature's own content for its page's built-in block (the Bible's chapters, the matchup
   * library, the membership plans): the route that loaded the feature renders it, with its data,
   * actions and access checks, exactly as on the native page.
   */
  builtIn?: ReactNode;
  /** The authored page's path, for native feed actions to return to it. */
  path?: string;
  /** The creator whose page this is, for their League clips' authorized video. */
  creatorId?: string;
  /** What League piece these nodes sit in: a Handbook, or one of its cards. */
  leagueParent?: "handbook" | "section";
  /**
   * Where a site path points instead of the creator's own page: a
   * template's example Home sends its links to the example's own pages.
   */
  linkTo?: (href: string) => string;
  /**
   * A template's example: text that is only the creator's name is marked, so onboarding can show
   * the name being typed there as it does in the header.
   */
  exampleName?: boolean;
  /**
   * The owner's private view of their own site, never the editor's canvas or a visitor's page:
   * an empty cover, picture, introduction or feed says how to fill it, linking to where they do.
   */
  owner?: OwnerLinks;
  /**
   * The editor's canvas: an empty picture draws a stand-in, while empty feeds and offers explain
   * how to fill them. Sections waiting for their first item stay on the page to be edited.
   */
  canvas?: boolean;
  /**
   * The sizes a list's pictures declare in place of "auto": a browser gives an auto-sized lazy
   * picture a fixed 300×150 size, so rows and mosaics, whose cards take each picture's own
   * proportions, name a width instead.
   */
  imageSizes?: string;
  /** The frame these nodes sit in, and its column's share of the site's width. */
  parentFrame?: PageNode;
  columnShare?: number;
  /** The website supplies live widgets; the editor supplies their inert server snapshots. */
  renderServerNode?: (
    node: PageNode,
    attributes: Record<string, unknown>,
    context: PageNodesProps,
  ) => ReactNode | undefined;
};

// Empty sections explain their source without looking as though they are still loading.
const canvasFeedNotes: Partial<Record<string, { title: string; detail: string }>> = {
  posts: {
    title: "No posts to show",
    detail: "Publish a post in Content, or change this section’s filters.",
  },
  content: {
    title: "No content to show",
    detail: "Publish content, or change what this section includes.",
  },
  collection: {
    title: "No items to show",
    detail: "Add published content to the collection selected for this section.",
  },
  matchups: { title: "No guides to show", detail: "Publish a matchup guide to fill this section." },
  products: {
    title: "No products to show",
    detail: "Add a product in Shop, or change this section’s filters.",
  },
  categories: {
    title: "No categories to show",
    detail: "Organize your products into categories in Shop.",
  },
  collections: {
    title: "No collections to show",
    detail: "Publish a collection in Content to fill this section.",
  },
};

/** Editor-only guidance; published pages continue to omit empty sections. */
function CanvasFeedEmpty({ title, detail }: { title: string; detail: string }) {
  return (
    <div className="drate-page-feed-empty">
      <strong>{title}</strong>
      <p>{detail}</p>
    </div>
  );
}

/** A site path keeps an owner's preview; everything else is left as written. */
function internalLink(href: string, preview: boolean, linkTo?: (href: string) => string) {
  const target = canonicalFeaturePath(href);
  if (linkTo && target.startsWith("/") && !target.startsWith("//")) return linkTo(target);
  if (!preview || !target.startsWith("/")) return target;
  const url = new URL(target, "https://drate.invalid");
  url.searchParams.set("preview", "1");
  return url.pathname + url.search + url.hash;
}

/** A name's first two initials, the stand-in for a picture not added yet. */
/** A card's access tag: Paid or Members only while locked, Free beside locked ones. */
function accessBadge(record: Item, records: readonly Item[]) {
  if (record.locked)
    return (
      <span className="drate-page-badge">{record.access === "paid" ? "Paid" : "Members only"}</span>
    );
  return record.access === "free" && records.some((entry) => entry.locked) ? (
    <span className="drate-page-badge drate-page-badge-free">Free</span>
  ) : null;
}

function initialsOf(name = "") {
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
}

const intervalLabel = (interval: string) =>
  interval === "month"
    ? "/ month"
    : interval === "year"
      ? "/ year"
      : interval === "per_creation"
        ? "/ creation"
        : "one time";

/**
 * An authored page's elements, rendered from validated page data with trusted components: the
 * markup, classes and attributes that the editor's canvas, its styles and the page's motion read.
 */
export function PageNodes(props: PageNodesProps) {
  const { nodes, bindings, experiences, preview, item, creator, member, textStyles } = props;
  const champion = bindings.champion;
  const { section, index, priorityImageId, openingSectionIds, images } = props;
  // The features this render can show; conditional elements follow them.
  const enabled = new Set(experiences.filter((entry) => entry.enabled).map((entry) => entry.kind));
  const child = (overrides: Partial<PageNodesProps> & { nodes: PageNode[] }) => (
    <PageNodes {...props} {...overrides} />
  );
  return nodes.map((own) => {
    // A linked element draws its shared style beneath its own settings (element-styles.ts).
    const node = elementStyled(own, props.elementStyles);
    if (!pageNodeVisible(node, enabled)) return null;
    // Text takes its site-wide text style first; the element's own typography overrides it. In a
    // League card, text reads like the Handbook's prose instead, unless the element is styled.
    const style =
      node.kind === "text" && (!props.leagueParent || node.style?.textStyle)
        ? withTextStyle(node.style, textStyles?.[textStyleOf(node)])
        : node.style;
    const share = pageColumnShare(props.parentFrame, node, props.columnShare ?? 1);
    const attributes = {
      "data-page-node-id": node.id,
      "data-page-node-kind": node.kind,
      ...pageNodeTreatments(style),
      ...(node.kind === "updates"
        ? {
            "data-page-case": undefined,
            "data-post-density": node.postDensity ?? "comfortable",
            "data-post-layout": node.postLayout ?? "grid",
            "data-post-columns":
              node.source === "posts" && node.presentation === "posts"
                ? (node.postColumns ?? 1)
                : undefined,
          }
        : {}),
      // A storefront theme's announcement bar, which the site lays above its header.
      ...(node.kind === "frame" &&
      node.themeSection?.kind === "announcement-bar" &&
      node.themeSection.place === "header"
        ? { "data-page-announcement": "" }
        : {}),
      // A theme section's content keeps within the site's own width, as its header does.
      ...(node.kind === "frame" &&
      !style?.container &&
      style?.maxWidth &&
      node.id.startsWith("v2-") &&
      (node.themeSection || (section && node.id === `${section}-inner`))
        ? { "data-page-contained": "" }
        : {}),
      style: {
        ...pageNodeStyle(style),
        ...(node.kind === "text" && !node.bind ? pageFluidTextFit(node.text, style, share) : {}),
      },
      // Animations run from these (usePageMotion); the config is validated page data.
      ...(node.motion && {
        "data-motion-config": JSON.stringify(node.motion),
        "data-motion-group": section ?? node.id,
        "data-motion-index": index,
        "data-motion-entrance": isEntrance(node.motion.trigger) ? "" : undefined,
      }),
    };
    const serverNode = props.renderServerNode?.(node, attributes, props);
    if (serverNode !== undefined) return serverNode;
    if (node.kind === "tabs" || node.kind === "accordion") {
      // Feature-disabled panels leave with their labels, and the chosen panel keeps its identity.
      const children = node.children.filter((panel) => pageNodeVisible(panel, enabled));
      const initialId = node.children[node.initialIndex]?.id;
      const initialIndex = children.findIndex((panel) => panel.id === initialId);
      return (
        <PageInteraction
          key={node.id}
          node={{
            ...node,
            children,
            initialIndex: initialIndex < 0 && node.kind === "tabs" ? 0 : initialIndex,
          }}
          attributes={attributes}
          renderPanel={(panel) => child({ nodes: [panel], section: section ?? node.id })}
        />
      );
    }
    if (node.kind === "frame") {
      if (node.hideWhenEmpty && !props.canvas) {
        // Offers count as a feed: a membership section waits for its first active plan.
        const feeds = walkPageNodes({ nodes: node.children }).filter(
          (entry) =>
            (entry.kind === "collection" ||
              entry.kind === "item" ||
              entry.kind === "memberships") &&
            pageNodeVisible(entry, enabled),
        );
        if (
          feeds.length &&
          feeds.every((feed) =>
            feed.kind === "memberships"
              ? !bindings.plans[feed.id]?.length
              : !bindings.items[feed.id]?.length,
          )
        )
          return null;
      }
      return (
        <div
          key={node.id}
          {...attributes}
          id={node.anchor}
          className="drate-page-node drate-page-frame"
          data-page-layout={node.layout}
          data-page-mobile={node.style?.mobile ?? "stack"}
        >
          {child({
            nodes: node.children,
            section: section ?? node.id,
            parentFrame: node,
            columnShare: share,
          })}
        </div>
      );
    }
    if (node.kind === "text" && node.formatted && !node.bind) {
      const html = formattedTextHtml(node.formatted, node.text, node.tag !== "p", (text) =>
        resolvePageText(text, { creator, item, member, champion }),
      );
      if (html !== undefined) {
        const Tag = node.tag === "p" ? "div" : node.tag;
        return (
          <Tag
            key={node.id}
            {...attributes}
            className="drate-page-node drate-page-text drate-page-formatted"
            dangerouslySetInnerHTML={{ __html: html }}
          />
        );
      }
    }
    if (node.kind === "text" && node.rich && !node.bind) {
      // Guide prose: bold, highlights and game icons with their tooltips, from validated tokens.
      const text = resolvePageText(node.text, { creator, item, member, champion });
      const league = bindings.league ?? emptyLeague;
      const Tag = node.list ? (node.list === "number" ? "ol" : "ul") : node.tag;
      return text ? (
        <Tag
          key={node.id}
          {...attributes}
          data-page-accent-words={node.highlight && !node.list ? "" : undefined}
          className={`drate-page-node drate-page-text${node.list ? " drate-page-list" : ""}${props.leagueParent ? " bh-text" : ""}`}
        >
          <Suspense>
            <RichText
              text={text}
              list={!!node.list}
              league={league}
              notes={resolvePageTooltipEntries(node.notes, { creator, item, member, champion })}
              highlight={node.highlight}
            />
          </Suspense>
        </Tag>
      ) : null;
    }
    if (node.kind === "text") {
      const text = node.bind
        ? pageItemField(item, node.bind)
        : resolvePageText(node.text, { creator, item, member, champion });
      if (node.list) {
        const List = node.list === "number" ? "ol" : "ul";
        const entries = pageListItems(text);
        return entries.length > 0 ? (
          <List
            key={node.id}
            {...attributes}
            className="drate-page-node drate-page-text drate-page-list"
          >
            {entries.map((entry, position) => (
              <li key={position}>{entry}</li>
            ))}
          </List>
        ) : null;
      }
      const Tag = node.tag;
      const accented = (words: string) =>
        pageAccentParts(words, node.highlight).map((part, position) =>
          part.accent ? (
            <span key={position} className="drate-page-inline-accent">
              {part.text}
            </span>
          ) : (
            part.text
          ),
        );
      // The introduction is the owner's to write, in place; visitors never see the prompt. Only
      // text that is the introduction alone: words written around it stay as visitors read them.
      const ownIntroduction =
        props.owner && !node.bind && /^\s*\{\{\s*creator\.description\s*\}\}\s*$/.test(node.text);
      if (ownIntroduction && props.owner)
        return (
          <WhenEditable
            key={node.id}
            fallback={
              text.trim() ? (
                <Tag {...attributes} className="drate-page-node drate-page-text">
                  {text}
                </Tag>
              ) : (
                <Tag {...attributes} className="drate-page-node drate-page-text">
                  <a className="drate-owner-prompt" href={props.owner.bio}>
                    <Plus size={14} strokeWidth={2} aria-hidden="true" />
                    Add an introduction
                  </a>
                </Tag>
              )
            }
          >
            <div {...attributes} className="drate-page-node drate-page-text">
              <OwnerIntro
                creatorId={props.owner.creatorId}
                text={creator?.description?.trim() ?? ""}
              />
            </div>
          </WhenEditable>
        );
      return text ? (
        <Tag
          key={node.id}
          {...attributes}
          data-page-accent-words={node.highlight ? "" : undefined}
          data-preview-name={
            props.exampleName && node.text.trim() === "{{creator.name}}" ? "" : undefined
          }
          data-preview-text={
            props.exampleName &&
            !node.bind &&
            (/\{\{\s*creator\.(name|description)\s*\}\}/.test(node.text) ||
              node.id === "hero-intro")
              ? node.text
              : undefined
          }
          // A list's price (a product card's, say) converts with the visitor's chosen currency.
          data-money={node.bind === "price" && !item?.comparePrice ? "" : undefined}
          // A matchup's difficulty takes its level's color as its accent.
          data-difficulty={(node.bind === "difficulty" && item?.difficulty) || undefined}
          className="drate-page-node drate-page-text"
        >
          {node.bind === "price" && item?.comparePrice ? (
            <>
              <span data-money="">{text}</span>{" "}
              <s
                className="drate-page-regular-price"
                data-money=""
                aria-label={`Regular price ${item.comparePrice}`}
              >
                {item.comparePrice}
              </s>
            </>
          ) : node.linkToItem && item?.url && !item.sample ? (
            <a
              className="drate-page-inline-link"
              href={internalLink(item.url, preview, props.linkTo)}
            >
              {accented(text)}
            </a>
          ) : node.bind || !node.links?.length ? (
            accented(text)
          ) : (
            pageTextLinkParts(node.text, node.links).map((piece, position) => {
              const words = accented(
                resolvePageText(piece.text, { creator, item, member, champion }),
              );
              return piece.href ? (
                <a
                  key={position}
                  className="drate-page-inline-link"
                  href={internalLink(piece.href, preview, props.linkTo)}
                >
                  {words}
                </a>
              ) : (
                <Fragment key={position}>{words}</Fragment>
              );
            })
          )}
        </Tag>
      ) : null;
    }
    if (node.kind === "image") {
      const src = node.bind
        ? node.bind === "portrait"
          ? item?.portrait
          : item?.image
        : resolvePageImage(node, creator);
      const href = node.linkToItem ? (item?.sample ? undefined : item?.url) : node.href;
      const linked = (picture: ReactNode) =>
        href ? (
          <a
            key={node.id}
            className="drate-page-image-link"
            href={internalLink(href, preview, props.linkTo)}
            aria-label={node.alt || item?.title || "View image"}
          >
            {picture}
          </a>
        ) : (
          picture
        );
      // The owner's empty picture and cover take a picture in place.
      if (node.variable === "creator.logo" && !src && props.owner && !props.exampleName) {
        const slot = ownerSlot(node, props.parentFrame);
        return (
          <WhenEditable
            key={node.id}
            fallback={
              <a
                {...attributes}
                className="drate-page-node drate-page-image drate-page-initials drate-owner-photo"
                href={props.owner.brand}
                aria-label={slot.add}
              >
                <span aria-hidden="true">{initialsOf(creator?.name)}</span>
                <span className="drate-owner-photo-badge" aria-hidden="true">
                  <Camera size={13} strokeWidth={2} />
                </span>
              </a>
            }
          >
            <OwnerPicture
              creatorId={props.owner.creatorId}
              slot={slot}
              alt={node.alt}
              initials={initialsOf(creator?.name)}
              attributes={attributes}
            />
          </WhenEditable>
        );
      }
      if (node.variable === "creator.heroImage" && !src && props.owner) {
        // A small slot (a link page's portrait) shows the icon alone; its name stays for readers.
        const slot = ownerSlot(node, props.parentFrame);
        return (
          <WhenEditable
            key={node.id}
            fallback={
              <a
                {...attributes}
                className="drate-page-node drate-page-image drate-owner-cover"
                href={props.owner.brand}
                aria-label={slot.add}
              >
                <ImagePlus size={18} strokeWidth={1.75} aria-hidden="true" />
                <span aria-hidden="true">{slot.add}</span>
              </a>
            }
          >
            <OwnerPicture
              creatorId={props.owner.creatorId}
              slot={slot}
              alt={node.alt}
              attributes={attributes}
            />
          </WhenEditable>
        );
      }
      // The canvas shows where a picture goes; a stand-in never draws outside it.
      if (node.variable === "creator.logo" && (!src || props.exampleName))
        return (
          <span
            key={node.id}
            {...attributes}
            className="drate-page-node drate-page-image drate-page-initials"
            data-preview-initials={props.exampleName ? "" : undefined}
            aria-hidden="true"
          >
            {initialsOf(creator?.name)}
          </span>
        );
      const standIn = canvasPlaceholderSetting(src);
      if (standIn !== null || (!src && !node.bind && props.canvas))
        return props.canvas ? (
          <CanvasImage key={node.id} attributes={attributes} setting={standIn ?? ""} />
        ) : null;
      // Start the first row of bound artwork before a visitor scrolls to it, when its feed opens
      // the page. Post images still use the authorized media route.
      // A creator's own choice (design mode's Loading) comes first: "first" loads with the page,
      // a list's first six pictures with it; "later" waits until the picture scrolls near.
      const eager =
        node.loading === "first"
          ? !node.bind || (index !== undefined && index < 6)
          : node.loading !== "later" &&
            (node.id === priorityImageId ||
              (!!node.bind &&
                index !== undefined &&
                index < 3 &&
                (!openingSectionIds || !section || openingSectionIds.includes(section))));
      // A published picture loads from the CDN, at the size each screen needs when it has
      // copies. A card's post picture asks its route for that size instead.
      const image = src ? images[src] : undefined;
      const srcset = image ? image.srcset : node.bind ? routeSrcset(src) : undefined;
      const sizes = props.imageSizes ?? (eager ? eagerImageSizes : lazyImageSizes);
      // A card whose post has no picture keeps the picture's space, so a feed's rows stay even;
      // a members-only post shows a lock there.
      if (!src)
        return node.bind && item
          ? linked(
              <div
                key={node.id}
                {...attributes}
                className="drate-page-node drate-page-image drate-page-image-empty"
                data-page-image-locked={item.locked ? "" : undefined}
                aria-hidden="true"
              />,
            )
          : null;
      // Mature artwork (a product option) stays blurred inside its own frame.
      if (node.bind && item?.mature)
        return linked(
          <span
            key={node.id}
            {...attributes}
            className="drate-page-node drate-page-image drate-page-image-veiled"
          >
            <img
              src={image?.src ?? src}
              srcSet={srcset}
              sizes={srcset ? sizes : undefined}
              alt={node.alt || item.title || ""}
              loading={eager ? "eager" : "lazy"}
              decoding="async"
            />
          </span>,
        );
      // The owner changes their own cover or profile picture in place, from a control on the
      // picture itself. The picture sits in a box of its own that carries the element's classes
      // and attributes, so it lays out as visitors see it, whatever else shares its frame.
      if (
        props.owner &&
        !props.exampleName &&
        (node.variable === "creator.logo" || node.variable === "creator.heroImage")
      ) {
        const slot = ownerSlot(node, props.parentFrame);
        const change = (
          <WhenEditable fallback={null}>
            <OwnerPictureChange creatorId={props.owner.creatorId} slot={slot} />
          </WhenEditable>
        );
        return (
          <span
            key={node.id}
            {...attributes}
            className="drate-page-node drate-page-image drate-owner-slot"
          >
            {slot.top && change}
            <img
              className="drate-owner-picture"
              src={image?.src ?? src}
              srcSet={srcset}
              sizes={srcset ? sizes : undefined}
              alt={node.alt || ""}
              loading={eager ? "eager" : "lazy"}
              decoding="async"
              fetchPriority={node.id === priorityImageId ? "high" : undefined}
            />
            {!slot.top && change}
          </span>
        );
      }
      const picture = (
        <img
          key={node.id}
          {...attributes}
          data-preview-image={props.exampleName ? node.variable : undefined}
          className="drate-page-node drate-page-image"
          src={image?.src ?? src}
          srcSet={srcset}
          sizes={srcset ? sizes : undefined}
          alt={node.alt || (node.bind ? item?.title : "") || ""}
          loading={eager ? "eager" : "lazy"}
          decoding="async"
          fetchPriority={node.id === priorityImageId ? "high" : undefined}
        />
      );
      // A product's second photo, which a theme can fade in on hover (page-presentation.css).
      const hover = node.bind ? item?.hoverImage : undefined;
      if (!hover) return linked(picture);
      const hoverImage = images[hover];
      return linked(
        <Fragment key={node.id}>
          {picture}
          <img
            className="drate-page-image-hover"
            style={attributes.style}
            src={hoverImage?.src ?? hover}
            srcSet={hoverImage?.srcset ?? routeSrcset(hover)}
            sizes={props.imageSizes ?? lazyImageSizes}
            alt=""
            aria-hidden="true"
            loading="lazy"
            decoding="async"
          />
        </Fragment>,
      );
    }
    if (node.kind === "link") {
      // Illustrative products and categories have no destination or checkout.
      if (node.bind && item?.sample) return null;
      const href = node.bind
        ? item?.url
        : node.pageId
          ? bindings.pageLinks[node.pageId]
          : node.href;
      const label = resolvePageText(node.label, { creator, item, member, champion }) || "Open";
      const thumbnail = node.thumbnail ? images[node.thumbnail] : undefined;
      // A code copies when pressed, with or without a destination.
      if (node.code && !node.bind)
        return (
          <CodeLink
            key={node.id}
            node={{ ...node, code: node.code }}
            label={label}
            href={href ? internalLink(href, preview, props.linkTo) : undefined}
            thumbnail={thumbnail}
            attributes={attributes}
          />
        );
      return href ? (
        <a
          key={node.id}
          {...attributes}
          {...pageLinkAttributes(node)}
          className="drate-page-node drate-page-link"
          href={internalLink(href, preview, props.linkTo)}
        >
          {node.subtitle || node.thumbnail ? (
            <PageLinkContent
              node={node}
              label={label}
              thumbnail={thumbnail}
              arrow={<span aria-hidden="true">↗</span>}
            />
          ) : (
            <>
              {/* One text node, so the markup carries no separator comments. */}
              {`${label} `}
              <span aria-hidden="true">↗</span>
            </>
          )}
        </a>
      ) : null;
    }
    if (node.kind === "updates")
      return (
        <div key={node.id} {...attributes} className="drate-page-node drate-page-updates">
          <UpdatesFeed
            node={node}
            data={bindings.updates[node.id] ?? { items: [], canPublish: false, manageHref: null }}
            path={props.path ?? "/"}
            creatorName={creator?.name}
            linkTo={props.linkTo}
            canvas={props.canvas}
            ownerPost={props.owner?.post}
          />
        </div>
      );
    if (node.kind === "collection" || node.kind === "item") {
      const records = bindings.items[node.id] ?? [];
      // The editor's canvas selects a piece rather than opening it.
      const fullView = node.kind === "collection" && !!node.fullView && !props.canvas;
      if (node.kind === "collection" && node.search)
        return (
          <div
            key={node.id}
            {...attributes}
            className="drate-page-node drate-page-directory"
            data-page-mobile={node.style?.mobile ?? "stack"}
          >
            <SearchableCards
              id={node.id}
              mobile={node.style?.mobile}
              label={node.search}
              source={node.kind === "collection" ? node.source.kind : undefined}
              cards={records.map((record, position) => ({
                ...record,
                content: (
                  <article
                    key={record.id}
                    className="drate-page-card"
                    data-page-item-id={record.id}
                  >
                    {child({
                      nodes: node.template,
                      item: record,
                      section: section ?? node.id,
                      index: position,
                      imageSizes: repeaterImageSizes(node.style),
                    })}
                    {accessBadge(record, records)}
                  </article>
                ),
              }))}
            />
          </div>
        );
      return records.length > 0 ? (
        <div
          key={node.id}
          {...attributes}
          className={
            node.kind === "collection" ? "drate-page-node drate-page-repeater" : "drate-page-node"
          }
          data-page-mobile={node.style?.mobile ?? "stack"}
          data-page-source={node.kind === "collection" ? node.source.kind : undefined}
          data-page-full-view={fullView ? "" : undefined}
          data-editor-examples={
            props.canvas && records.some((record) => record.example) ? "shown" : undefined
          }
        >
          {records.map((record, position) => (
            <article
              key={record.id}
              className="drate-page-card"
              data-page-item-id={record.id}
              // A piece without a link of its own opens large (picture-full-view.tsx).
              {...(fullView && record.image && !record.url
                ? {
                    "data-full-view-src": record.image,
                    "data-full-view-title": record.title,
                    "data-full-view-caption": record.summary,
                  }
                : {})}
            >
              {child({
                nodes: node.template,
                item: record,
                section: section ?? node.id,
                index: position,
                imageSizes:
                  node.style?.arrangement === "rows" || node.style?.arrangement === "mosaic"
                    ? "(max-width: 760px) 100vw, 50vw"
                    : repeaterImageSizes(node.style),
              })}
              {fullView && record.image && !record.url && (
                <button
                  type="button"
                  className="drate-page-full-view-open"
                  data-full-view-open=""
                  aria-label={record.title ? `View ${record.title}` : "View picture"}
                />
              )}
              {accessBadge(record, records)}
              {record.sample && <span className="drate-page-badge">Sample</span>}
              {record.example && <span className="drate-page-badge">Example</span>}
              {(record.soldOut || record.saving) && (
                <span className="drate-page-tags">
                  {record.soldOut && <span className="drate-page-tag">Sold out</span>}
                  {record.saving && <span className="drate-page-tag">Save {record.saving}</span>}
                </span>
              )}
              {!!record.pictures && (
                <span className="drate-page-badge drate-page-badge-count">
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <rect x="3" y="3" width="18" height="18" rx="2" />
                    <circle cx="9" cy="9" r="2" />
                    <path d="m21 15-3.1-3.1a2 2 0 0 0-2.8 0L6 21" />
                  </svg>
                  {record.pictures}
                  <span className="sr-only">{record.pictures === 1 ? "picture" : "pictures"}</span>
                </span>
              )}
            </article>
          ))}
          {fullView && <PictureFullView id={`${node.id}-full-view`} />}
        </div>
      ) : props.canvas ? (
        <div
          key={node.id}
          {...attributes}
          className="drate-page-node"
          data-page-mobile={node.style?.mobile ?? "stack"}
          data-page-source={node.kind === "collection" ? node.source.kind : undefined}
          data-editor-examples={
            node.kind === "collection" &&
            (node.source.kind === "posts" || node.source.kind === "content") &&
            !node.source.contentIds?.length
              ? "available"
              : undefined
          }
        >
          <CanvasFeedEmpty
            {...((node.kind === "collection" ? canvasFeedNotes[node.source.kind] : undefined) ?? {
              title: "No items to show",
              detail: "Choose published content for this section in its settings.",
            })}
          />
        </div>
      ) : null;
    }
    if (node.kind === "memberships") {
      const plans = bindings.plans[node.id] ?? [];
      return plans.length > 0 ? (
        <section
          key={node.id}
          {...attributes}
          className="drate-page-node drate-page-memberships"
          aria-labelledby={node.heading ? `page-membership-${node.id}` : undefined}
          aria-label={node.heading ? undefined : "Memberships"}
          data-editor-examples={
            props.canvas && plans.some((plan) => plan.example) ? "shown" : undefined
          }
        >
          {node.heading && <h2 id={`page-membership-${node.id}`}>{node.heading}</h2>}
          <div className="drate-page-offers">
            {plans.map((plan) => {
              // The plan's own perks follow its description; before perks, a description of
              // short lines was the list.
              const perks = planOwnPerks(plan);
              const lines = perks.length ? perks : planBenefits(plan.description);
              const members = planMembersLabel(plan.memberCount);
              const savings = planSavingsLabel(plan);
              const join = (planId: string) =>
                internalLink(
                  node.joinHref ||
                    ("joinLinks" in bindings ? bindings.joinLinks?.[planId] : undefined) ||
                    (preview && !props.linkTo
                      ? "/join"
                      : `/app/account?plan=${encodeURIComponent(planId)}`),
                  preview,
                  props.linkTo,
                );
              return (
                <article key={plan.id} className="drate-page-offer">
                  {plan.example && <span className="drate-page-badge">Example</span>}
                  <h3>{plan.title}</h3>
                  {members && <PlanMembers label={members} />}
                  <PlanPrice
                    className="drate-page-price"
                    price={planPrice(plan.priceCents, plan.currency)}
                    cadence={intervalLabel(plan.interval)}
                    intro={planIntroLabel(plan, planPrice)}
                  />
                  {(perks.length || !lines.length) && plan.description.trim() && (
                    <p className="drate-page-benefits">{plan.description}</p>
                  )}
                  <PlanPerks
                    perks={lines}
                    label={`${plan.title} includes`}
                    className="drate-page-benefit-list"
                  />
                  {plan.interval === "one_time" && (
                    <p className="drate-page-benefits">{planAccessLabel(plan)}</p>
                  )}
                  {plan.mail && (
                    <p className="drate-page-benefits">
                      {`Mailed to you each month · ${shipsToPhrase(plan.mail.countries)}`}
                    </p>
                  )}
                  {plan.remaining !== undefined && (
                    <p className="drate-page-benefits">
                      {plan.remaining ? `Limited · ${plan.remaining} left` : "Full right now"}
                    </p>
                  )}
                  {plan.remaining === 0 && !preview ? (
                    <span className="button button-outline" aria-disabled="true">
                      {`${plan.title} is full`}
                    </span>
                  ) : (
                    <a className="button button-primary" href={join(plan.id)}>
                      {`${node.joinLabel || `${plan.interval === "one_time" ? "Get" : "Join"} ${plan.title}`} `}
                      <span aria-hidden="true">→</span>
                    </a>
                  )}
                  {savings && (
                    <PlanSavings
                      savings={{
                        text: savings.text,
                        // Paying yearly joins the yearly plan; an example plan is never for sale.
                        href: savings.planId && !plan.example ? join(savings.planId) : null,
                      }}
                    />
                  )}
                </article>
              );
            })}
          </div>
        </section>
      ) : props.canvas ? (
        <section
          key={node.id}
          {...attributes}
          className="drate-page-node drate-page-memberships"
          aria-label="Memberships"
          data-editor-examples="available"
        >
          {node.heading && <h2>{node.heading}</h2>}
          <CanvasFeedEmpty
            title="No memberships to show"
            detail="Create a plan in Memberships. Your active plans will appear here."
          />
        </section>
      ) : null;
    }
    if (node.kind === "video" && node.background) {
      // A background plays at once, muted and looping. React leaves `muted` out of server HTML,
      // and browsers only start a muted video by themselves, so the element is written as markup.
      const playback = bindings.media[node.id];
      const src = playback?.url ?? node.src;
      if (!src) return null;
      const attribute = (value: string) =>
        value.replaceAll("&", "&amp;").replaceAll('"', "&quot;").replaceAll("<", "&lt;");
      return (
        <div
          key={node.id}
          {...attributes}
          className="drate-page-node drate-page-video-background"
          aria-hidden="true"
          dangerouslySetInnerHTML={{
            __html: `<video src="${attribute(src)}"${playback?.poster ? ` poster="${attribute(playback.poster)}"` : ""} autoplay muted loop playsinline disablepictureinpicture preload="auto" tabindex="-1"></video>`,
          }}
        />
      );
    }
    if (node.kind === "video" || node.kind === "audio") {
      // An upload plays in the site's own player once this visitor may play it; a link waits
      // for Play.
      const playback = bindings.media[node.id];
      if (playback)
        return (
          <div key={node.id} {...attributes} className="drate-page-node drate-page-media">
            <MediaPlayer playback={playback} />
          </div>
        );
      if (node.kind === "video" && node.src)
        // A file of Drate's own plays in the browser's player, its first frame showing until then.
        return (
          <div key={node.id} {...attributes} className="drate-page-node drate-page-video-file">
            <video
              src={node.src}
              title={node.title || undefined}
              controls
              playsInline
              preload="metadata"
            />
          </div>
        );
      return node.kind === "video" && node.url ? (
        <PageVideo key={node.id} url={node.url} title={node.title} attributes={attributes} />
      ) : null;
    }
    if (node.kind === "newsletter")
      return (
        <div key={node.id} {...attributes} className="drate-page-node">
          <Newsletter
            id={node.id}
            pagePath={props.path}
            creatorName={creator?.name ?? "the creator"}
            buttonLabel={node.buttonLabel}
            askName={node.askName}
            preview={preview}
          />
        </div>
      );
    if (node.kind === "contactForm")
      return (
        <div key={node.id} {...attributes} className="drate-page-node">
          <ContactForm
            id={node.id}
            pagePath={props.path}
            buttonLabel={node.buttonLabel}
            preview={preview}
          />
        </div>
      );
    // Tips draw only where the site takes them; the bindings say so, with its settings.
    if (node.kind === "tip")
      return bindings.tips ? (
        <div key={node.id} {...attributes} className="drate-page-node">
          <TipBox
            id={node.id}
            heading={resolvePageText(node.heading, { creator, item, member, champion })}
            buttonLabel={node.buttonLabel}
            picker={node.picker}
            settings={bindings.tips.settings}
            creatorName={creator?.name ?? "the creator"}
            pagePath={props.path}
            preview={preview}
            example={!!props.exampleName}
          />
        </div>
      ) : null;
    if (node.kind === "supporters")
      return bindings.tips ? (
        <div key={node.id} {...attributes} className="drate-page-node">
          <Supporters
            heading={resolvePageText(node.heading, { creator, item, member, champion })}
            limit={node.limit}
            sample={!!props.canvas || preview || !!props.exampleName}
          />
        </div>
      ) : null;
    if (node.kind === "stream") {
      // Live on Twitch now, else the latest YouTube video; the server decided as it served it.
      const stream = bindings.streams[node.id];
      if (!stream) return null;
      const twitch = node.twitch && twitchChannelLogin(node.twitch);
      return stream.status === "live" ? (
        <PageVideo
          key={node.id}
          url={stream.url}
          title={node.title || `${creator?.name ?? stream.channel} is live`}
          badge={{ label: "Live on Twitch", live: true }}
          autoplay={node.autoplay}
          attributes={attributes}
        />
      ) : stream.status === "video" ? (
        <PageVideo
          key={node.id}
          url={stream.url}
          title={stream.title || node.title || `${creator?.name ?? "Latest videos"} on YouTube`}
          badge={{ label: "Latest video" }}
          also={twitch ? { label: "Follow on Twitch", href: twitchChannelUrl(twitch) } : undefined}
          autoplay={node.autoplay}
          attributes={attributes}
        />
      ) : (
        <PageVideo
          key={node.id}
          url={stream.url}
          title={node.title || `${creator?.name ?? stream.channel} on Twitch`}
          badge={{ label: "Offline" }}
          autoplay={node.autoplay}
          attributes={attributes}
        />
      );
    }
    if (node.kind === "socials") {
      // The website's own social links; the canvas shows where they'll sit until there are some,
      // and the owner's private view where to add them. Visitors see nothing until then.
      const sample = !creator?.socials?.length && !!props.canvas;
      const socials = sample ? sampleSocials : (creator?.socials ?? []);
      if (props.owner && !sample)
        return (
          <WhenEditable
            key={node.id}
            fallback={
              socials.length ? (
                <div
                  {...attributes}
                  className="drate-page-node drate-page-socials"
                  data-size={node.size}
                  data-shape={node.shape}
                >
                  <SocialLinks socials={socials} />
                </div>
              ) : (
                <div {...attributes} className="drate-page-node drate-page-text">
                  <a className="drate-owner-prompt" href={props.owner.socials}>
                    <Plus size={14} strokeWidth={2} aria-hidden="true" />
                    Add your socials
                  </a>
                </div>
              )
            }
          >
            <OwnerSocials
              creatorId={props.owner.creatorId}
              socials={socials}
              id={node.id}
              attributes={attributes}
              size={node.size}
              shape={node.shape}
            />
          </WhenEditable>
        );
      return socials.length ? (
        <div
          key={node.id}
          {...attributes}
          className="drate-page-node drate-page-socials"
          data-size={node.size}
          data-shape={node.shape}
          data-sample={sample ? "" : undefined}
          title={sample ? "Add your social links in Settings › Website" : undefined}
        >
          <SocialLinks socials={socials} />
        </div>
      ) : null;
    }
    if (node.kind === "podcast") {
      // Shows only while podcast RSS is published; a member's own feed loads in the browser.
      const show = bindings.podcast;
      return show ? (
        <PodcastFollow
          key={node.id}
          show={show}
          title={node.title}
          signedIn={!!member}
          attributes={attributes}
        />
      ) : null;
    }
    // These widgets need the site's server-function runtime. Its wrapper supplies them above.
    if (
      node.kind === "abilityReference" ||
      node.kind === "liveMatchup" ||
      node.kind === "liveMatchupControl"
    )
      return null;
    if (node.kind === "leagueHandbook")
      return (
        <Suspense key={node.id}>
          <LeagueHandbook
            node={node}
            attributes={attributes}
            path={props.path}
            sections={node.children.flatMap((entry) =>
              entry.kind === "leagueSection" ? [{ id: entry.id, title: entry.title }] : [],
            )}
            link={(href) => internalLink(href, preview, props.linkTo)}
          >
            {child({ nodes: node.children, section: section ?? node.id, leagueParent: "handbook" })}
          </LeagueHandbook>
        </Suspense>
      );
    if (node.kind === "leagueSection") {
      const card = (
        <Suspense key={node.id}>
          <LeagueSection
            node={node}
            attributes={attributes}
            nested={props.leagueParent === "section"}
          >
            {child({ nodes: node.children, section: section ?? node.id, leagueParent: "section" })}
          </LeagueSection>
        </Suspense>
      );
      // Outside a Handbook, a card still takes the Handbook's colors from one around it.
      return props.leagueParent ? (
        card
      ) : (
        <div key={node.id} className="bh drate-league-scope">
          {card}
        </div>
      );
    }
    if (node.kind === "leagueBlock") {
      const block = (
        <Suspense key={node.id}>
          <LeagueBlockView
            node={node}
            attributes={attributes}
            league={bindings.league ?? emptyLeague}
            creatorId={props.creatorId ?? ""}
          />
        </Suspense>
      );
      return props.leagueParent ? (
        block
      ) : (
        <div key={node.id} className="bh drate-league-scope">
          {block}
        </div>
      );
    }
    // One of the creator's components: its elements as this instance sets them. A component never
    // published shows nothing to visitors.
    if (node.kind === "component") {
      const definition = bindings.components?.[node.componentId];
      return definition ? (
        <div key={node.id} {...attributes} className="drate-page-node drate-page-component">
          {child({ nodes: componentInstanceNodes(definition, node), section: section ?? node.id })}
        </div>
      ) : null;
    }
    // The guide its address names, drawn by Matchups with its own access checks (the guide route
    // renders it into builtIn).
    if (node.kind === "matchupGuide")
      return props.builtIn === null ? null : (
        <section key={node.id} {...attributes} className="drate-page-node drate-page-built-in">
          {props.builtIn ?? <NativePending name="matchups" />}
        </section>
      );
    // A built-in page's own content (the Bible, Matchups, Membership...) among the page's sections.
    // Null leaves it out: the page is an ordinary one while its feature is off.
    if (node.kind === "builtIn")
      return props.builtIn === null ? null : (
        <section
          key={node.id}
          {...attributes}
          className="drate-page-node drate-page-built-in"
          data-density={node.density}
        >
          {props.builtIn ?? <NativePending name={node.page} />}
        </section>
      );
    const experience = experiences.find((entry) => entry.kind === node.experience && entry.enabled);
    const route = experienceCatalog.find((entry) => entry.kind === node.experience)?.route;
    return experience && route ? (
      <section
        key={node.id}
        {...attributes}
        className="drate-page-node drate-page-feature"
        aria-labelledby={`page-feature-${node.id}`}
      >
        <h2 id={`page-feature-${node.id}`}>{node.heading || experience.navTitle}</h2>
        {experience.intro && <p className="drate-page-benefits">{experience.intro}</p>}
        <a className="button button-primary" href={internalLink(route, preview, props.linkTo)}>
          {`Explore ${experience.navTitle} `}
          <span aria-hidden="true">→</span>
        </a>
      </section>
    ) : null;
  });
}
